import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
type Ember = {
  x: number;
  y: number;
  r: number;
  depth: number;
  vx: number;
  vy: number;
  phase: number;
  hue: number;
};
type Smoke = {
  x: number;
  y: number;
  r: number;
  depth: number;
  vx: number;
  phase: number;
};

// Fire-like embers and slow smoke hanging over the editorial scroll.
// Three depth layers drift at different speeds as the page scrolls, for a sense of space.
export function AmbientParticles() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (reduce) return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    const count = w < 768 ? 40 : 80;
    const embers: Ember[] = Array.from({
      length: count
    }, () => {
      const depth = Math.random(); // 0 far … 1 near
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.6 + depth * 2.2,
        depth,
        vx: (Math.random() - 0.5) * 6,
        vy: -(6 + depth * 22),
        phase: Math.random() * Math.PI * 2,
        hue: 28 + Math.random() * 18
      };
    });
    const smoke: Smoke[] = Array.from({
      length: 6
    }, (_, i) => ({
      x: Math.random() * w,
      y: h * (0.3 + Math.random() * 0.8),
      r: 220 + Math.random() * 260,
      depth: i / 6,
      vx: (Math.random() - 0.5) * 8,
      phase: Math.random() * Math.PI * 2
    }));

    // Cursor: embers part gently around it, more so while the page is moving
    const pointer = {
      x: -9999,
      y: -9999
    };
    const onPointer = (e: PointerEvent) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
    };
    const onPointerLeave = () => {
      pointer.x = -9999;
      pointer.y = -9999;
    };
    window.addEventListener('pointermove', onPointer, {
      passive: true
    });
    document.addEventListener('pointerleave', onPointerLeave);
    const push = new Float32Array(count * 2);
    let lastScroll = window.scrollY;
    let velocity = 0;
    let last = performance.now();
    let frame = 0;
    let time = 0;
    const wrap = (v: number, max: number, pad: number) => v < -pad ? v + max + pad * 2 : v > max + pad ? v - max - pad * 2 : v;
    const tick = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      time += dt;
      const sy = window.scrollY;
      const dScroll = sy - lastScroll;
      lastScroll = sy;
      velocity += (dScroll - velocity) * 0.15;
      ctx.clearRect(0, 0, w, h);

      // Smoke: large, very faint, warm-grey breaths
      for (const s of smoke) {
        s.x = wrap(s.x + s.vx * dt, w, s.r);
        s.y = wrap(s.y - dScroll * (0.15 + s.depth * 0.25), h, s.r);
        const a = 0.035 + Math.sin(time * 0.3 + s.phase) * 0.015;
        const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r);
        g.addColorStop(0, `rgba(214,196,170,${a})`);
        g.addColorStop(1, 'rgba(214,196,170,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Embers: rise, sway, flicker; nearer ones move further with the scroll
      ctx.globalCompositeOperation = 'lighter';
      const stir = 1 + Math.min(3, Math.abs(velocity) * 0.15);
      embers.forEach((e, idx) => {
        // Soft repulsion from the cursor, eased so embers drift away rather than jump
        const dx = e.x - pointer.x;
        const dy = e.y - pointer.y;
        const dist = Math.hypot(dx, dy);
        const radius = 160;
        if (dist < radius && dist > 0.001) {
          const f = (1 - dist / radius) ** 2 * 140 * stir * (0.4 + e.depth);
          push[idx * 2] += dx / dist * f * dt;
          push[idx * 2 + 1] += dy / dist * f * dt;
        }
        push[idx * 2] *= 0.94;
        push[idx * 2 + 1] *= 0.94;
        e.x = wrap(e.x + (e.vx + Math.sin(time * 0.8 + e.phase) * 8) * dt + push[idx * 2], w, 20);
        e.y = wrap(e.y + e.vy * dt - velocity * (0.3 + e.depth * 0.9) + push[idx * 2 + 1], h, 20);
        const flicker = 0.45 + 0.55 * Math.abs(Math.sin(time * (1.5 + e.depth * 2) + e.phase));
        const alpha = (0.25 + e.depth * 0.55) * flicker;
        const glow = e.r * 5;
        const g = ctx.createRadialGradient(e.x, e.y, 0, e.x, e.y, glow);
        g.addColorStop(0, `hsla(${e.hue},95%,70%,${alpha})`);
        g.addColorStop(0.25, `hsla(${e.hue},90%,55%,${alpha * 0.35})`);
        g.addColorStop(1, `hsla(${e.hue},90%,50%,0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(e.x, e.y, glow, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalCompositeOperation = 'source-over';
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointer);
      document.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [reduce]);
  if (reduce) return null;
  return <canvas ref={ref} className="pointer-events-none fixed inset-0 z-[5] h-full w-full" aria-hidden="true" data-id="element-1227" data-mp="245p17rybqmu1" />;
}